/**
 * MarketplaceSettingsPanel — grouped collapsible admin UI for Wave 1.5 + 1.6 marketplace settings.
 */

import React, { useMemo } from "react";
import { Form, Input, InputNumber, Space, Switch, Tag, Typography } from "antd";
import styled from "styled-components";
import ExpandableSections from "@/shared/components/ExpandableSections.jsx";
import {
  MARKETPLACE_SECTIONS,
  MARKETPLACE_SETTING_KEYS,
  getMarketplaceFieldMeta,
} from "../utils/marketplaceSettingsMeta.js";
import {
  getMarketplaceFieldHelp,
  getMarketplaceSectionHelp,
} from "../utils/marketplaceSettingsHelp.js";
import PricingFieldHelpBubble from "./PricingFieldHelpBubble.jsx";
import MarketplaceExperimentStartAtField from "./MarketplaceExperimentStartAtField.jsx";
import MarketplaceOpsAlertsField from "./MarketplaceOpsAlertsField.jsx";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const FieldBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionHint = styled(Paragraph)`
  && {
    margin-top: 0;
    margin-bottom: 12px;
  }
`;

const PanelIntro = styled(Paragraph)`
  && {
    margin-bottom: 16px;
  }
`;

/**
 * @param {object} props
 * @param {object[]} [props.settings] - Loaded marketplace setting rows (any category)
 */
const MarketplaceSettingsPanel = ({ settings = [] }) => {
  const settingsByKey = useMemo(() => {
    const map = {};
    settings.forEach((setting) => {
      map[setting.key] = setting;
    });
    return map;
  }, [settings]);

  const fieldsBySection = useMemo(() => {
    /** @type {Record<string, string[]>} */
    const grouped = {};
    for (const section of MARKETPLACE_SECTIONS) {
      grouped[section.key] = [];
    }

    const keys = new Set([
      ...MARKETPLACE_SETTING_KEYS,
      ...settings.map((s) => s.key),
    ]);

    keys.forEach((key) => {
      const meta = getMarketplaceFieldMeta(key);
      if (!meta) {
        return;
      }
      const sectionKey = meta.section || "experiment";
      if (!grouped[sectionKey]) {
        grouped[sectionKey] = [];
      }
      grouped[sectionKey].push(key);
    });

    return grouped;
  }, [settings]);

  const renderNumberField = (key) => {
    const meta = getMarketplaceFieldMeta(key);
    const dbSetting = settingsByKey[key];
    const fieldHelp = getMarketplaceFieldHelp(key);
    const description = meta.description || dbSetting?.description || "";

    return (
      <FieldBlock key={key}>
        <Form.Item
          name={key}
          label={
            <Space size="small" align="center">
              <span>{meta.label}</span>
              {fieldHelp && <PricingFieldHelpBubble help={fieldHelp} />}
            </Space>
          }
          extra={
            description ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {description}
              </Text>
            ) : null
          }
        >
          <InputNumber
            min={meta.metadata?.min}
            max={meta.metadata?.max}
            step={1}
            style={{ width: "100%" }}
            addonAfter={meta.metadata?.unit === "hours" ? "hours" : undefined}
          />
        </Form.Item>
        {meta.metadata?.unit && (
          <Tag color="blue">Unit: {meta.metadata.unit}</Tag>
        )}
      </FieldBlock>
    );
  };

  const renderTypedField = (key) => {
    const meta = getMarketplaceFieldMeta(key);
    if (!meta) return null;
    const dbSetting = settingsByKey[key];
    const fieldHelp = getMarketplaceFieldHelp(key);
    const description = meta.description || dbSetting?.description || "";

    let control;
    if (meta.type === "boolean") {
      control = <Switch />;
    } else if (meta.type === "number") {
      control = (
        <InputNumber
          min={meta.metadata?.min}
          max={meta.metadata?.max}
          step={1}
          style={{ width: "100%" }}
        />
      );
    } else if (meta.type === "object") {
      control = (
        <TextArea
          rows={8}
          style={{ fontFamily: "monospace", fontSize: 12 }}
          placeholder='{"email":"entry.123","idDocumentType":"entry.456"}'
        />
      );
    } else {
      control = (
        <Input
          placeholder={
            key === "marketplace.providerApply.googleFormUrl"
              ? "Paste Google Form viewform URL"
              : undefined
          }
        />
      );
    }

    return (
      <FieldBlock key={key}>
        <Form.Item
          name={key}
          valuePropName={meta.type === "boolean" ? "checked" : "value"}
          label={
            <Space size="small" align="center">
              <span>{meta.label}</span>
              {fieldHelp && <PricingFieldHelpBubble help={fieldHelp} />}
            </Space>
          }
          extra={
            description ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {description}
              </Text>
            ) : null
          }
        >
          {control}
        </Form.Item>
        {key === "marketplace.providerApply.googleFormUrl" ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Setting key: <Text code>{key}</Text>
          </Text>
        ) : null}
        {key === "marketplace.providerApply.googleFormPrefill" ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Setting key: <Text code>{key}</Text> — JSON object saved with Save
            All Changes.
          </Text>
        ) : null}
      </FieldBlock>
    );
  };

  const expandableSections = useMemo(() => {
    return MARKETPLACE_SECTIONS.map((section) => {
      const fieldKeys = fieldsBySection[section.key] || [];
      const sectionHelp = getMarketplaceSectionHelp(section.key);

      return {
        key: section.key,
        title: (
          <Space size="small">
            <span>{section.title}</span>
            {sectionHelp && (
              <PricingFieldHelpBubble
                size="section"
                help={{
                  title: section.title,
                  summary: sectionHelp.summary,
                  details: sectionHelp.details,
                }}
              />
            )}
          </Space>
        ),
        forceRender: true,
        children: (
          <>
            {section.hint && (
              <SectionHint type="secondary">{section.hint}</SectionHint>
            )}

            {section.key === "experiment" && (
              <>
                <FieldBlock>
                  <MarketplaceExperimentStartAtField
                    setting={settingsByKey["marketplace.experiment.startAt"]}
                  />
                </FieldBlock>
                {fieldKeys
                  .filter((key) => key !== "marketplace.experiment.startAt")
                  .map((key) => renderNumberField(key))}
              </>
            )}

            {section.key === "leadTime" &&
              fieldKeys.map((key) => renderNumberField(key))}

            {section.key === "opsAlerts" && (
              <FieldBlock>
                <MarketplaceOpsAlertsField
                  setting={settingsByKey["marketplace.ops.notifyOnInterest"]}
                />
              </FieldBlock>
            )}

            {section.key === "providerOnboarding" &&
              fieldKeys.map((key) => renderTypedField(key))}
          </>
        ),
      };
    });
  }, [fieldsBySection, settingsByKey]);

  if (settings.length === 0 && MARKETPLACE_SETTING_KEYS.length === 0) {
    return null;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <PanelIntro type="secondary">
        Marketplace settings — Wave 1.5 cohort/lead-time/ops alerts, plus Wave
        1.6 provider onboarding (Google Form URL + prefill). Expand a section to
        edit. All values save with <Text strong>Save All Changes</Text>.
      </PanelIntro>

      <ExpandableSections
        defaultOpenKeys={["experiment", "opsAlerts", "providerOnboarding"]}
        sections={expandableSections}
      />
    </Space>
  );
};

export default MarketplaceSettingsPanel;
